import React, { useState } from "react";
import Modal from "react-bootstrap/Modal";
import { UploadCloud, X } from "lucide-react";
import { Button } from "react-bootstrap";
import { notifyError, notifyInfo, notifySuccess } from "../../utils/helper";
import { uploadCompanyCSVfile } from "../../requests";
import { startLoader, stopLoader } from "../../_store/reducers/loaderReducer";
import { useDispatch } from "react-redux";
import "../../_css/UserManagement.css";

const AddCompanyModal = ({ show, onHide, onCompanyCreated }) => {
    const [videoFile, setVideoFile] = useState(null);
    const [isHovered, setIsHovered] = useState(false);
    const dispatch = useDispatch();

    // Reset file when modal closes
    const handleClose = () => {
        setVideoFile(null);
        onHide();
    };

    // Reset file when modal opens/closes
    React.useEffect(() => {
        if (!show) {
            setVideoFile(null);
        }
    }, [show]);

    const handleUpload = async () => {
        if (!videoFile) {
            notifyInfo("Please select a CSV file");
            return;
        }
        if (videoFile.type !== "text/csv" && !videoFile.name.endsWith(".csv")) {
            notifyError("Please select a valid CSV file");
            return;
        }
        const formData = new FormData();
        formData.append("file", videoFile);
        dispatch(startLoader());

        try {
            const response = await uploadCompanyCSVfile(formData);
            // Axios wraps the backend response in 'data'
            const { success, message, failed, inserted } = response.data;
            if (success) {
                // Check if there are partial failures even if success is true
                if (failed && failed.length > 0) {
                    // Construct a message from the failed array
                    const errorMessages = failed
                        .map(item => `${item.company}: ${item.reason}`)
                        .join("\n");

                    notifyError(`\n${errorMessages}`);
                } else {
                    notifySuccess(message || "Companies uploaded successfully");
                }

                // Only reset and close if at least one record was actually inserted
                if (inserted > 0) {
                    setVideoFile(null);
                    onHide();
                    onCompanyCreated();
                }
            } else {
                notifyError(message || "Upload failed");
            }
        } catch (err) {
            // Handle cases where the server returns a 400/500 status code
            const backendErrors = err.response?.data?.failed;
            if (backendErrors && backendErrors.length > 0) {
                const errorList = backendErrors.map(f => `${f.company}: ${f.reason}`).join(", ");
                notifyError(`Errors: ${errorList}`);
            } else {
                notifyError(err.response?.data?.message || "Server error occurred");
            }
        } finally {
            dispatch(stopLoader());
        }
    };


    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];

            // Validate file type
            if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
                notifyError("Please select a CSV file");
                e.target.value = ''; // Clear the file input
                return;
            }
            setVideoFile(file);
        }
    };

    return (
        <Modal
            show={show}
            onHide={handleClose}
            centered
            dialogClassName="modal-38"
        >
            <Modal.Body>
                <div className="add-company-modal-container">
                    <div className="add-company-modal-header">
                        <X onClick={handleClose} className="add-company-modal-close" />
                        <span className="add-company-modal-title">
                            To Add Company Upload CSV
                        </span>
                    </div>
                    <div>
                        {/* File Upload Section */}
                        <div className="add-company-modal-upload-area">
                            <UploadCloud size={48} color="#000000" />

                            {!videoFile ? (
                                <>
                                    <label
                                        htmlFor={`file-upload-0`}
                                        className="add-company-modal-upload-label"
                                    >
                                        Select a CSV file to Upload
                                    </label>
                                    <p className="add-company-modal-upload-subtext">
                                        Or drag and drop
                                    </p>
                                </>
                            ) : (
                                <>
                                    <p className="add-company-modal-filename">
                                        {videoFile.name}
                                    </p>
                                    <label
                                        htmlFor={`file-upload-0`}
                                        className="add-company-modal-change-file"
                                    >
                                        Change file
                                    </label>
                                </>
                            )}

                            <input
                                id={`file-upload-0`}
                                type="file"
                                accept=".csv"
                                className="add-company-modal-file-input"
                                onChange={handleFileChange}
                            />
                        </div>

                    </div>

                    {/* button */}
                    <div className="add-company-modal-buttons">
                        <button
                            className="add-company-modal-back-button"
                            onClick={handleClose}
                        >
                            Back
                        </button>
                        <button
                            className="add-company-modal-next-button"
                            onClick={handleUpload}
                        >
                            Add Company
                        </button>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    )
};

export default AddCompanyModal;